import React, { ReactNode, useEffect, useMemo, useState, useCallback } from "react";

// UX
import CardTreatmentOrderInterfaceUX from "./CardTreatmentOrderInterfaceUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Utils
import { Checkbox } from "semantic-ui-react";
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Interface
import { useIntl } from "react-intl";
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  SaveType,
  FilterSearchType,
  WidthsType,
  GenerateTableHeader,
  PaymentStatusOptions,
  OrderStatusOptions,
  OrderTypeOptions,
} from "./sequence/TreatmentOrderInterface";

// Serializer
import TreatmentOrderInterfaceSerializer from "issara-sdk/types/TreatmentOrderSerializerForCreate_apps_TRT";
import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";

// Type
type CardTreatmentOrderInterfaceProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  TreatmentOrderInterfaceSequence: State["TreatmentOrderInterfaceSequence"];
  languageUX: "en" | "th";
} & PickedProps;

const CardTreatmentOrderInterface = (props: CardTreatmentOrderInterfaceProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);
  const [selectedTreatmentOrder, setSelectedTreatmentOrder] = useState<any[]>([]);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "TreatmentOrderInterface", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data ?? ""}</div>;
  };

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      let newData = (props.TreatmentOrderInterfaceSequence?.TreatmentOrderInterfaceList || []).map(
        (item: TreatmentOrderInterfaceSerializer) => item.id
      );

      setSelectedTreatmentOrder(newData);
    } else {
      setSelectedTreatmentOrder([]);
    }
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "TreatmentOrderInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      page: 0,
    });

    setSelectedTreatmentOrder([]);
  };

  const handleSave = (type: SaveType) => {
    props.runSequence({
      sequence: "TreatmentOrderInterface",
      action: "SAVE",
      selectedItems: selectedTreatmentOrder,
      cardKey: CARD_NAME,
      type,
    });

    setSelectedTreatmentOrder([]);
  };

  const handleClear = () => {
    props.runSequence({
      sequence: "TreatmentOrderInterface",
      action: "CLEAR",
      cardKey: CARD_NAME,
    });

    setSelectedTreatmentOrder([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;
    props.setProp(`TreatmentOrderInterfaceSequence.searchTreatmentOrder.${name}`, data?.value);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`TreatmentOrderInterfaceSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "TreatmentOrderInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      [key]: value,
    });

    setSelectedTreatmentOrder([]);
    props.setProp(`TreatmentOrderInterfaceSequence.selectedItems`, []);
  };

  const handleDisabledTrigger = useMemo(() => {
    if (selectedTreatmentOrder.length === 0) return true;
  }, [selectedTreatmentOrder, props.TreatmentOrderInterfaceSequence?.TreatmentOrderInterfaceList]);

  const formatEmr = useCallback(
    (encounterData: EncounterSerializer | undefined, emrId: number | undefined | null) => {
      const id = encounterData?.id ? encounterData.id : "-";
      const number = encounterData?.number ? encounterData?.number : "-";
      const patient = encounterData?.patient_name;

      return `[EMR ${emrId}] [${id} : ${number}] ${patient}`;
    },
    [props.TreatmentOrderInterfaceSequence?.encounterList]
  );

  // useMemo
  const treatmentOrderInterfaceList = useMemo(() => {
    return (props.TreatmentOrderInterfaceSequence?.TreatmentOrderInterfaceList || [])
      .map(convertToRow)
      .map((item: TreatmentOrderInterfaceSerializer) => {
        const paymentStatus = (PaymentStatusOptions || []).find(
          (data: any) => data.value === item.order_payment_status
        );

        const orderStatus = (OrderStatusOptions || []).find(
          (data: any) => data?.key === item.order_status
        );

        const orderType = (OrderTypeOptions || []).find(
          (data: any) => data?.value === item.order_type
        );

        const encounterData = (props.TreatmentOrderInterfaceSequence?.encounterList || []).find(
          (data: EncounterSerializer) => data?.id === item.encounter
        );

        return {
          ...item,
          _checked: columnTableCenter(
            <Checkbox
              checked={selectedTreatmentOrder.includes(item.id)}
              // disabled={}
              onChange={(e: any, v: any) => {
                if (v.checked) {
                  setSelectedTreatmentOrder([...selectedTreatmentOrder, item.id]);
                } else {
                  let newData = selectedTreatmentOrder.filter((key: any) => key !== item.id);
                  setSelectedTreatmentOrder(newData);
                }
              }}
            />,
            { padding: "10px" }
          ),
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof TreatmentOrderInterfaceSerializer, ReactNode>>, header) => {
              const key = header.accessor;

              if (key === "order_summary_detail") {
                data[key] = <div dangerouslySetInnerHTML={{ __html: item[key] || "" }} />;
              } else if (dateTimeColumns.includes(key)) {
                data[key] = columnTableCenter(item[key] ? dateToStringBE(item[key]) : item[key]);
              } else if (key === "order_status") {
                data[key] = columnTableCenter(orderStatus?.text);
              } else if (key === "order_type") {
                data[key] = columnTableCenter(orderType?.text);
              } else if (key === "order_payment_status") {
                data[key] = columnTableCenter(paymentStatus?.text);
              } else if (leftColumns.includes(key)) {
                data[key] = item[key];
              } else if (conditionColumns.includes(key)) {
                data[key] = columnTableCenter(item[key] ? "Yes" : "No");
              } else if (divisionColumns.includes(key)) {
                const division = (props.masterOptions?.division || []).find(
                  (div: any) => div?.value === item[key]
                );

                data[key] = columnTableCenter(division?.text);
              } else if (doctorColumns.includes(key)) {
                const doctor = (props.TreatmentOrderInterfaceSequence?.doctorOptions || []).find(
                  (doc: any) => doc?.id === item[key]
                );

                data[key] = columnTableCenter(doctor?.name_code);
              } else if (userColumns.includes(key)) {
                const user = (props.TreatmentOrderInterfaceSequence?.userOptions || []).find(
                  (usr: any) => usr?.id === item[key]
                );

                data[key] = columnTableCenter(user?.full_name_code);
              } else if (key === "emr") {
                data[key] = columnTableCenter(formatEmr(encounterData, item[key]));
              } else if (key === "order_summary_detail_cache") {
                const cache = { ...item[key] };

                if (cache?.order_summary_detail) {
                  const noTags = cache.order_summary_detail
                    .replace(/<br\s*\/?>/gi, ", ")
                    .replace(/<[^>]+>/g, "");

                  cache.order_summary_detail = noTags.trim();
                }

                data[key] = JSON.stringify(cache, null, 2);
              } else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            },
            {}
          ),
        };
      });
  }, [
    selectedTreatmentOrder,
    props.TreatmentOrderInterfaceSequence?.TreatmentOrderInterfaceList,
    tableHeaders,
    PaymentStatusOptions,
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardTreatmentOrderInterfaceUX
        onChangeData={handleChangeData}
        onClearFilter={handleClear}
        searchTreatmentOrder={props.TreatmentOrderInterfaceSequence?.searchTreatmentOrder}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedTreatmentOrder.length ===
                    (props.TreatmentOrderInterfaceSequence?.TreatmentOrderInterfaceList || [])
                      .length &&
                  (props.TreatmentOrderInterfaceSequence?.TreatmentOrderInterfaceList || [])
                    .length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        treatmentOrderInterfaceList={treatmentOrderInterfaceList}
        paginatedItems={props.TreatmentOrderInterfaceSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title={intl.formatMessage({ id: "ค้นหา" })}
            name="SEARCH"
            size="medium"
          />
        }
        triggerButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("TRIGGER")}
            disabled={handleDisabledTrigger}
            // data
            paramKey={`${CARD_NAME}_TRIGGER`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_TRIGGER`]}
            // config
            color="blue"
            title="Trigger"
            name="TRIGGER"
            size="medium"
          />
        }
      />
    </>
  );
};

CardTreatmentOrderInterface.displayName = "CardTreatmentOrderInterface";

export default React.memo(CardTreatmentOrderInterface);

const dateTimeColumns: Partial<keyof TreatmentOrderInterfaceSerializer>[] = [
  "order_receive_time",
  "doctor_edit_time",
  "order_ack_time",
  "order_perform_time",
  "created_utc",
  "edited_utc",
];

const leftColumns: Partial<keyof TreatmentOrderInterfaceSerializer>[] = [
  "diagnosis",
  "order_detail",
  "equipment_detail",
  "result",
  "complications",
];

const conditionColumns: Partial<keyof TreatmentOrderInterfaceSerializer>[] = [
  "need_approve",
  "is_advance",
  "is_operation_division",
  "is_out_perform_div",
  "is_seen",
  "is_technician",
];

const divisionColumns: Partial<keyof TreatmentOrderInterfaceSerializer>[] = [
  "order_div",
  "order_ack_div",
  "out_perform_div",
  "order_perform_div",
];

const userColumns: Partial<keyof TreatmentOrderInterfaceSerializer>[] = ["edit_user"];

const doctorColumns: Partial<keyof TreatmentOrderInterfaceSerializer>[] = ["edited_doctor"];

const columnWidths: WidthsType = {
  id: 70,
  created: 150,
  edited: 150,
  status_name: 150,
  order_perform_by_name: 200,
  order_time: 150,
  order_by_name: 200,
  order_receive_time: 150,
  patient_name: 200,
  patient_location: 180,
  order_summary_detail: 200,
  diagnosis: 250,
  start: 150,
  finish: 150,
  student_name: 200,
  result_approved_at: 150,
  result_approved_by_name: 200,
  order_ack_time: 150,
  order_perform_time: 150,
  order_detail: 200,
  equipment_detail: 200,
  result: 200,
  complications: 150,
  edited_doctor: 200,
  doctor_edit_time: 150,
  emr: 400,
  order_type: 200,
  order_status: 150,
  edit_user: 200,
  created_utc: 150,
  edited_utc: 150,
  order_summary_detail_cache: 250,
  to_be_performer_name: 150,
};

const convertToRow = (item: TreatmentOrderInterfaceSerializer) => {
  // const firstOrderItem = item.order_items?.[0] || {};
  const firstOrderItem = item.order_items?.[0] || null;

  return {
    ...item,
    // order_items_0_edit_user: firstOrderItem?.edit_user,
    order_items_0_created: firstOrderItem?.created,
    order_items_0_edited: firstOrderItem?.edited,
  };
};

const tableHeaders = GenerateTableHeader(columnWidths);
